import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  avKeys,
  fetchCohortDefinition,
  useCohortDefinitionStore,
} from '@verantos-dev/feature-analytical-variables';
import type { CohortDefinitionQueryData } from '@verantos-dev/feature-analytical-variables';

interface StoreHydrationParams {
  projectId: string;
  cohortId: string;
}

/**
 * Shared hydration logic for the socket hook.
 *
 * Fetches cohort definition for a version and calls `setFromServer`.
 * Uses `queryClient.fetchQuery` so it respects TQ caching and deduplication.
 * Pass `skipIfStagedChanges: true` to preserve local optimistic edits that
 * arrived during the async fetch (e.g. user typing while a socket event resolves).
 */
export const useStoreHydration = (params: StoreHydrationParams) => {
  const queryClient = useQueryClient();
  const setFromServer = useCohortDefinitionStore((s) => s.setFromServer);

  const hydrateVersion = useCallback(
    async (versionId: string, opts: { skipIfStagedChanges?: boolean } = {}) => {
      const { projectId, cohortId } = params;

      // Use shared query function with React Query caching
      const fetchedData = await queryClient.fetchQuery<CohortDefinitionQueryData>({
        queryKey: avKeys.list(versionId),
        queryFn: () => fetchCohortDefinition(projectId, cohortId, versionId),
      });

      // If the user edited while the fetch was in-flight, preserve their optimistic state.
      // TQ cache is still populated — only the store write is skipped.
      if (opts.skipIfStagedChanges && useCohortDefinitionStore.getState().hasStagedChanges) return;

      // Backend provides changeStatus/changedBy directly on AVs — no client-side diffing needed
      setFromServer(
        fetchedData.analyticalVariables,
        fetchedData.criteria,
        fetchedData.studyPeriod,
        {
          simpleEvents: fetchedData.simpleEvents,
          derivedEvents: fetchedData.derivedEvents,
          indexDate: fetchedData.indexDate,
          subjectVariables: fetchedData.subjectVariables,
        }
      );
    },
    [params, queryClient, setFromServer]
  );

  return { hydrateVersion, queryClient };
};
